import { Badge, Menu, Text, UnstyledButton } from '@mantine/core'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'

import { FaIcon } from '#components/FaIcon'
import { FancyMenuItem } from '#components/FancyMenuItem'
import { useUpgrade } from '#components/Modals'
import { useCanUpgrade } from '#components/Settings/Org/useBillingPlan'
import { useNavigation } from '#hooks'
import { useMst } from '#state'
import { ROUTE_PATTERNS } from '#util/pathConfigs'

import styles from './OrgSwitcher.module.scss'

const OrgMenuItem = ({
  orgName,
  orgId,
  isCurrent,
  onClick,
}: {
  orgName: string
  orgId: string
  isCurrent: boolean
  onClick: (id: string) => void
}) => (
  <Menu.Item
    className={cn(styles.switcher_workspace, {
      [styles.selected]: isCurrent,
    })}
    onClick={() => onClick(orgId)}
  >
    {orgName}
  </Menu.Item>
)

type OrgSwitcherProps = {
  variant?: 'standalone' | 'embedded'
  fullWidth?: boolean
}

export const OrgSwitcher = observer(function OrgSwitcher({
  variant = 'standalone',
  fullWidth = false,
}: OrgSwitcherProps) {
  const { user, currentOrg, switchOrgAndRelaunch: switchOrg } = useMst()
  const { selectedMembership } = user
  const { navigate } = useNavigation()
  const { upgrade } = useUpgrade()
  const canUpgrade = useCanUpgrade(currentOrg)
  const handleSelectOrg = (orgId: string) => {
    if (selectedMembership?.orgId !== orgId) {
      switchOrg(orgId)
    }
  }

  const mustUpgrade = !!currentOrg?.hasNoBillingAccount && canUpgrade

  const createWorkspace = () => {
    if (mustUpgrade && currentOrg) {
      upgrade(currentOrg)
    } else {
      navigate(ROUTE_PATTERNS.createWorkspace)
    }
  }

  return (
    <Menu position="bottom-start">
      <Menu.Target>
        <UnstyledButton
          px={10}
          py={6}
          className={cn(styles.switcher_current, {
            [styles.standalone]: variant === 'standalone',
            [styles.fullWidth]: fullWidth,
          })}
        >
          <Text flex={1} fw="bold" miw={0} truncate="end">
            {selectedMembership?.name ?? 'Select Show'}&nbsp;
          </Text>
          {variant === 'standalone' ? (
            <span className={styles.switcher_icon}>
              <i className="fa fa-caret-down" />
            </span>
          ) : (
            <FaIcon className={styles.switcher_icon} size="13" icon="fa-sort" />
          )}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown className={styles.switcher_list}>
        {user.sortedOrgMemberships.map((m) => (
          <OrgMenuItem
            key={m.orgId}
            orgId={m.orgId}
            orgName={m.name}
            onClick={handleSelectOrg}
            isCurrent={selectedMembership?.orgId === m.orgId}
          />
        ))}
        <Menu.Divider />
        <FancyMenuItem
          onClick={createWorkspace}
          disabled={currentOrg?.isLapsedStripeAccount}
          iconClass="fa-solid fa-plus"
          title="New workspace"
          badge={
            currentOrg?.isFreeStripeAccount ? (
              <Badge size="xs">PAID</Badge>
            ) : undefined
          }
        />
      </Menu.Dropdown>
    </Menu>
  )
})
